import { useState, useEffect, useMemo } from "react";
import { useConfigurationUpdater } from "../root/ConfigurationContext";
import type { Deal, DealPipeline, DealStage } from "../types";
import { getSupabaseClient } from "../providers/supabase/supabase";

export type Pipeline = DealPipeline;

export const DEFAULT_CRM_ID = "fe386f56-b27f-446f-86ec-39a03b64add1"; // Grupo Dotus

export const getActiveCrmId = (): string => {
  if (typeof window === "undefined") return DEFAULT_CRM_ID;
  const saved = localStorage.getItem("dotus_active_crm_id");
  if (!saved || saved === "00000000-0000-0000-0000-000000000001") {
    return DEFAULT_CRM_ID;
  }
  return saved;
};

export const getStorageKeyPipelines = (crmId = getActiveCrmId()) =>
  `dotus.${crmId}.pipelines`;
export const getStorageKeyCurrentId = (crmId = getActiveCrmId()) =>
  `dotus.${crmId}.current_pipeline_id`;

export const defaultSalesStages: DealStage[] = [
  { value: "entrada-de-prospect", label: "Entrada de Prospect" },
  { value: "tentando-contato", label: "Tentando Contato" },
  { value: "contato-realizado", label: "Contato Realizado" },
  { value: "qualificacao", label: "Qualificação" },
  { value: "qualificado", label: "Qualificado" },
  { value: "reuniao-agendada", label: "Reunião Agendada" },
  { value: "reuniao-realizada", label: "Reunião Realizada" },
  { value: "proposta-criada", label: "Proposta Criada" },
  { value: "negociacao", label: "Negociação" },
  { value: "venda", label: "Venda" },
];

export const defaultPipelines: DealPipeline[] = [
  {
    id: "funil-de-vendas",
    nome: "Funil de Vendas",
    descricao: "Funil padrão de vendas e negociações",
    etapas: defaultSalesStages,
  },
];

export const defaultPipeline: DealPipeline = defaultPipelines[0];

export const slugifyPipelineName = (name: string): string => {
  return (name || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

export const isDealInPipeline = (
  deal: Deal,
  currentPipeline?: DealPipeline,
  allPipelines?: DealPipeline[],
  currentPipelineId?: string,
): boolean => {
  if (!currentPipeline && !currentPipelineId) return true;

  const pipes =
    allPipelines && allPipelines.length > 0
      ? allPipelines
      : currentPipeline
        ? [currentPipeline]
        : [];
  const targetPipe =
    currentPipeline ||
    pipes.find(
      (p) =>
        String(p.id).toLowerCase() ===
          String(currentPipelineId).toLowerCase() ||
        slugifyPipelineName(p.nome) ===
          slugifyPipelineName(currentPipelineId || ""),
    ) ||
    pipes[0];

  const currentPipeId = targetPipe
    ? String(targetPipe.id).toLowerCase().trim()
    : String(currentPipelineId || "")
        .toLowerCase()
        .trim();
  const currentPipeName = targetPipe?.nome
    ? targetPipe.nome.toLowerCase().trim()
    : "";
  const currentPipeSlug = currentPipeName
    ? slugifyPipelineName(currentPipeName)
    : "";

  const isDefaultPipe =
    pipes.length === 0 ||
    !targetPipe ||
    String(targetPipe.id).toLowerCase() ===
      String(pipes[0]?.id).toLowerCase() ||
    targetPipe.nome?.toLowerCase() === pipes[0]?.nome?.toLowerCase() ||
    currentPipeSlug === "funil-de-vendas" ||
    currentPipeSlug.startsWith("funil-de-vendas") ||
    currentPipeSlug === "sdr-inbond";

  const dealFunilRaw = deal.funil_id;
  const dealFunil =
    dealFunilRaw !== undefined && dealFunilRaw !== null
      ? String(dealFunilRaw).toLowerCase().trim()
      : "";

  // 1. Deals sem funil_id ou no funil legado "principal"
  if (!dealFunil || dealFunil === "principal") {
    // Pertence ao funil padrão / inicial (SDR - INBOND)
    return isDefaultPipe;
  }

  // 2. Compara diretamente com o ID, nome ou slug do funil atual, ou funil equivalente em pipes
  const isMatchCurrent =
    dealFunil === currentPipeId ||
    (currentPipeName && dealFunil === currentPipeName) ||
    (currentPipeSlug && dealFunil === currentPipeSlug) ||
    (dealFunil.startsWith("funil-de-vendas") &&
      (currentPipeSlug.startsWith("funil-de-vendas") || isDefaultPipe)) ||
    pipes.some(
      (p) =>
        (slugifyPipelineName(p.nome) === currentPipeSlug ||
          (p.nome && p.nome.toLowerCase().trim() === currentPipeName)) &&
        (String(p.id).toLowerCase().trim() === dealFunil ||
          slugifyPipelineName(p.nome) === dealFunil ||
          (p.nome && p.nome.toLowerCase().trim() === dealFunil)),
    );

  if (isMatchCurrent) {
    return true;
  }

  // 3. Se o dealFunil bate com ALGUM OUTRO funil cadastrado na lista
  const belongsToOtherPipe = pipes.some((other) => {
    // Se 'other' for o mesmo funil que targetPipe (mesmo id, nome ou slug), não é outro funil!
    if (
      targetPipe &&
      (String(other.id).toLowerCase().trim() === currentPipeId ||
        (other.nome && other.nome.toLowerCase().trim() === currentPipeName) ||
        slugifyPipelineName(other.nome) === currentPipeSlug)
    ) {
      return false;
    }
    const otherId = String(other.id || "")
      .toLowerCase()
      .trim();
    const otherName = (other.nome || "").toLowerCase().trim();
    const otherSlug = slugifyPipelineName(otherName);
    return (
      dealFunil === otherId ||
      dealFunil === otherName ||
      dealFunil === otherSlug
    );
  });

  if (belongsToOtherPipe) {
    return false;
  }

  // 4. Se tem um funil desconhecido/órfão, exibe no funil padrão para nunca sumir
  return isDefaultPipe;
};

let isSyncingPipelines = false;

export const syncPipelinesToSupabase = async (pipeList?: DealPipeline[]) => {
  if (isSyncingPipelines) return;
  isSyncingPipelines = true;

  try {
    const client = getSupabaseClient();
    const {
      data: { session },
    } = await client.auth.getSession();
    if (!session?.user) return;

    const activeCrmId = getActiveCrmId();

    let list = pipeList;
    if (!list) {
      try {
        const saved = localStorage.getItem(getStorageKeyPipelines(activeCrmId));
        if (saved) {
          list = JSON.parse(saved);
        }
      } catch {}
      if (!list || !Array.isArray(list) || list.length === 0) {
        list = defaultPipelines;
      }
    }

    const uniqueList = deduplicatePipelines(list);

    for (let i = 0; i < uniqueList.length; i++) {
      const p = uniqueList[i];
      if (!p.nome) continue;

      // Usar limit(1) filtrando por crm_id
      const { data: existing } = await client
        .from("pipelines")
        .select("id")
        .eq("crm_id", activeCrmId)
        .eq("name", p.nome)
        .limit(1);

      let pipelineId = existing && existing.length > 0 ? existing[0].id : null;

      if (!pipelineId) {
        const { data: inserted, error: insertErr } = await client
          .from("pipelines")
          .insert({
            crm_id: activeCrmId,
            name: p.nome,
            description: p.descricao || "",
            is_default: i === 0,
            order_nr: i + 1,
          })
          .select("id")
          .single();
        if (insertErr || !inserted) continue;
        pipelineId = inserted.id;
      }

      if (pipelineId && Array.isArray(p.etapas) && p.etapas.length > 0) {
        for (let j = 0; j < p.etapas.length; j++) {
          const s = p.etapas[j];
          await client.from("pipeline_stages").upsert(
            {
              pipeline_id: pipelineId,
              name: s.label,
              slug: s.value,
              order_nr: j + 1,
            },
            { onConflict: "pipeline_id,slug" },
          );
        }
      }
    }
  } catch (err) {
    console.error("Erro ao sincronizar pipelines com Supabase:", err);
  } finally {
    isSyncingPipelines = false;
  }
};

export const generateStageSlug = (
  name: string,
  existingStages: DealStage[],
) => {
  let baseSlug = slugifyPipelineName(name);
  if (!baseSlug) baseSlug = "etapa";
  let slug = baseSlug;
  let counter = 1;
  while (existingStages.some((s) => s.value === slug)) {
    counter++;
    slug = `${baseSlug}-${counter}`;
  }
  return slug;
};

export const deduplicatePipelines = (list: DealPipeline[]): DealPipeline[] => {
  const seenNames = new Set<string>();
  const seenIds = new Set<string>();
  const result: DealPipeline[] = [];
  for (const p of list) {
    if (!p) continue;
    const nameKey = (p.nome || "").trim().toLowerCase();
    const idKey = (p.id || "").trim().toLowerCase();
    if (!nameKey || seenNames.has(nameKey) || (idKey && seenIds.has(idKey))) {
      continue;
    }
    seenNames.add(nameKey);
    if (idKey) seenIds.add(idKey);
    result.push(p);
  }
  return result.length > 0 ? result : defaultPipelines;
};

export const PIPELINES_CHANGED_EVENT = "dotus:pipelines_changed";

export const broadcastPipelinesChanged = (
  pipelines: DealPipeline[],
  activeId: string,
) => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(PIPELINES_CHANGED_EVENT, {
        detail: { pipelines, currentPipelineId: activeId },
      }),
    );
  }
};

export const usePipelines = () => {
  const updateConfiguration = useConfigurationUpdater();

  // Carrega APENAS do localStorage do CRM ativo — nunca do ConfigurationContext
  // (que pode conter dados de outro CRM e causar vazamento)
  const loadPipelinesFromStorage = (): DealPipeline[] => {
    try {
      const activeCrmId = getActiveCrmId();
      const saved = localStorage.getItem(getStorageKeyPipelines(activeCrmId));
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const mapped = parsed.map((pipe: DealPipeline) => ({
            ...pipe,
            etapas: (pipe.etapas || []).filter(
              (s: DealStage) =>
                s &&
                typeof s.label === "string" &&
                s.label.trim() !== "" &&
                typeof s.value === "string" &&
                s.value.trim() !== "" &&
                s.value !== "won" &&
                s.value !== "lost" &&
                s.value.toLowerCase() !== "ganho" &&
                s.value.toLowerCase() !== "perdido",
            ),
          }));
          // Se for o pipeline legado antigo com apenas as 4 etapas de "prospeccao"/"fechamento", descarta para não ocultar leads
          const isLegacyOld = mapped.some(
            (p) =>
              p.etapas.length <= 4 &&
              p.etapas.some(
                (s) => s.value === "prospeccao" || s.value === "fechamento",
              ),
          );
          if (isLegacyOld) {
            try {
              localStorage.removeItem(getStorageKeyPipelines(activeCrmId));
            } catch {}
          } else {
            const deduped = deduplicatePipelines(mapped);
            try {
              localStorage.setItem(
                getStorageKeyPipelines(activeCrmId),
                JSON.stringify(deduped),
              );
            } catch {}
            return deduped;
          }
        }
      }
    } catch {
      // fallback
    }
    // Fallback: defaultPipelines apenas — NÃO usar config.dealPipelines
    // pois ele pode conter pipelines de outro CRM (bug de vazamento entre tenants)
    return defaultPipelines;
  };

  const loadCurrentIdFromStorage = (): string => {
    try {
      const activeCrmId = getActiveCrmId();
      const saved = localStorage.getItem(getStorageKeyCurrentId(activeCrmId));
      if (saved) return saved;
    } catch {
      // fallback
    }
    return defaultPipeline.id;
  };

  const [pipelines, setPipelines] = useState<DealPipeline[]>(
    loadPipelinesFromStorage,
  );
  const [currentPipelineId, setCurrentPipelineId] = useState<string>(
    loadCurrentIdFromStorage,
  );
  // true enquanto aguarda resposta do Supabase na inicialização
  const [isLoadingPipelines, setIsLoadingPipelines] = useState<boolean>(true);

  // Escuta atualizações de outras instâncias de usePipelines ou SettingsPage
  useEffect(() => {
    const handlePipelinesChanged = (e: Event) => {
      const customEvent = e as CustomEvent<{
        pipelines: DealPipeline[];
        currentPipelineId?: string;
      }>;
      if (customEvent.detail?.pipelines) {
        setPipelines(customEvent.detail.pipelines);
        if (customEvent.detail.currentPipelineId) {
          setCurrentPipelineId(customEvent.detail.currentPipelineId);
        }
      }
    };

    window.addEventListener(PIPELINES_CHANGED_EVENT, handlePipelinesChanged);
    return () => {
      window.removeEventListener(
        PIPELINES_CHANGED_EVENT,
        handlePipelinesChanged,
      );
    };
  }, []);

  // Sincronização inicial com o Supabase — fonte de verdade por CRM
  // Garante que nunca exibiremos funis de outro CRM
  useEffect(() => {
    let isMounted = true;
    const initSupabasePipelines = async () => {
      try {
        const client = getSupabaseClient();
        const {
          data: { session },
        } = await client.auth.getSession();
        if (!session?.user) {
          if (isMounted) setIsLoadingPipelines(false);
          return;
        }

        // Captura o CRM ativo no momento em que o efeito roda (após o reload)
        const activeCrmId = getActiveCrmId();

        const { data: remotePipes, error } = await client
          .from("pipelines")
          .select("*, pipeline_stages(*)")
          .eq("crm_id", activeCrmId)
          .order("order_nr", { ascending: true });

        if (error) {
          console.warn("Aviso ao buscar pipelines no Supabase:", error.message);
          if (isMounted) setIsLoadingPipelines(false);
          return;
        }

        if (remotePipes && remotePipes.length > 0) {
          // Deduplica rigorosamente pelo nome do funil
          const seenNames = new Set<string>();
          const uniqueRemotePipes: any[] = [];
          for (const rp of remotePipes) {
            const key = (rp.name || "").trim().toLowerCase();
            if (!key || seenNames.has(key)) continue;
            seenNames.add(key);
            uniqueRemotePipes.push(rp);
          }

          const mapped: DealPipeline[] = uniqueRemotePipes.map((rp: any) => ({
            id: String(rp.id),
            nome: rp.name,
            descricao: rp.description || "",
            etapas: (rp.pipeline_stages || [])
              .sort((a: any, b: any) => (a.order_nr ?? 0) - (b.order_nr ?? 0))
              .map((st: any) => ({
                id: String(st.id),
                slug: st.slug,
                value: st.slug,
                label: st.name,
              })),
          }));

          if (isMounted) {
            // Substitui completamente quaisquer pipelines em memória (elimina dados sujos de outro CRM)
            setPipelines(mapped);
            try {
              localStorage.setItem(
                getStorageKeyPipelines(activeCrmId),
                JSON.stringify(mapped),
              );
            } catch {}

            // Alinha o currentPipelineId se ele for um slug ou ID antigo
            setCurrentPipelineId((prevId) => {
              const matching = mapped.find(
                (p) =>
                  String(p.id) === String(prevId) ||
                  slugifyPipelineName(p.nome) === slugifyPipelineName(prevId) ||
                  p.nome.toLowerCase() === (prevId || "").toLowerCase(),
              );
              const validId = matching?.id || mapped[0]?.id || prevId;
              try {
                localStorage.setItem(
                  getStorageKeyCurrentId(activeCrmId),
                  validId,
                );
              } catch {}
              return validId;
            });
          }
        } else if (isMounted) {
          // Nenhum funil no banco para este CRM → garante que não exibimos funis de outro CRM
          // Limpa o localStorage sujo e usa default
          const savedLocal = localStorage.getItem(
            getStorageKeyPipelines(activeCrmId),
          );
          if (!savedLocal) {
            setPipelines(defaultPipelines);
          }
        }
      } catch (err) {
        console.error("Erro ao carregar pipelines do Supabase:", err);
      } finally {
        if (isMounted) setIsLoadingPipelines(false);
      }
    };

    initSupabasePipelines();
    return () => {
      isMounted = false;
    };
  }, []);

  const currentPipeline = useMemo(() => {
    if (!pipelines || pipelines.length === 0) return defaultPipeline;
    const match =
      pipelines.find(
        (p) =>
          String(p.id).toLowerCase() ===
            String(currentPipelineId).toLowerCase() ||
          slugifyPipelineName(p.nome) ===
            slugifyPipelineName(currentPipelineId) ||
          p.nome.toLowerCase() === String(currentPipelineId).toLowerCase(),
      ) ||
      pipelines[0] ||
      defaultPipeline;
    return match;
  }, [pipelines, currentPipelineId]);

  const syncState = (newPipelines: DealPipeline[], newActiveId: string) => {
    const activeCrmId = getActiveCrmId();
    setPipelines(newPipelines);
    setCurrentPipelineId(newActiveId);

    const active =
      newPipelines.find((p) => p.id === newActiveId) || newPipelines[0];

    try {
      localStorage.setItem(
        getStorageKeyPipelines(activeCrmId),
        JSON.stringify(newPipelines),
      );
      localStorage.setItem(getStorageKeyCurrentId(activeCrmId), newActiveId);
    } catch (err) {
      console.error("Erro ao salvar no localStorage:", err);
    }

    broadcastPipelinesChanged(newPipelines, newActiveId);

    if (active) {
      updateConfiguration((prev) => ({
        ...prev,
        dealPipelines: newPipelines,
        dealStages: active.etapas,
        currentPipelineId: newActiveId,
      }));
    }
  };

  const handleSelectPipeline = (id: string) => {
    syncState(pipelines, id);
  };

  const getDbPipelineId = async (
    client: any,
    pipelineId: string,
    pipeName?: string,
  ): Promise<number | null> => {
    const numId = Number(pipelineId);
    if (!isNaN(numId) && numId > 0) return numId;

    const activeCrmId = getActiveCrmId();
    if (pipeName) {
      const { data } = await client
        .from("pipelines")
        .select("id")
        .eq("crm_id", activeCrmId)
        .eq("name", pipeName)
        .limit(1);
      if (data && data.length > 0) return data[0].id;
    }

    return null;
  };

  const addPipeline = async (nome: string, descricao?: string) => {
    const cleanName = nome.trim();
    if (!cleanName) return;

    try {
      const client = getSupabaseClient();
      const {
        data: { session },
      } = await client.auth.getSession();

      let createdId: string | null = null;

      if (session?.user) {
        const activeCrmId = getActiveCrmId();
        // 1. Insere o funil no Supabase com crm_id
        const { data: inserted, error } = await client
          .from("pipelines")
          .insert({
            crm_id: activeCrmId,
            name: cleanName,
            description: descricao?.trim() || "",
            is_default: pipelines.length === 0,
            order_nr: pipelines.length + 1,
          })
          .select("id")
          .single();

        if (!error && inserted) {
          createdId = String(inserted.id);

          // 2. Insere as etapas padrão no Supabase
          await client.from("pipeline_stages").insert(
            defaultSalesStages.map((s, idx) => ({
              pipeline_id: inserted.id,
              name: s.label,
              slug: s.value,
              order_nr: idx + 1,
            })),
          );
        }
      }

      const newId = createdId || slugifyPipelineName(cleanName) || "funil";
      const newPipe: DealPipeline = {
        id: newId,
        nome: cleanName,
        descricao: descricao || "",
        etapas: [...defaultSalesStages],
      };

      const next = [...pipelines, newPipe];
      syncState(next, newPipe.id);
      return newPipe;
    } catch (err) {
      console.error("Erro ao criar funil no Supabase:", err);
    }
  };

  const deletePipeline = async (pipelineId: string) => {
    if (pipelines.length <= 1) return;
    const targetPipe = pipelines.find((p) => p.id === pipelineId);

    const next = pipelines.filter((p) => p.id !== pipelineId);
    const nextActiveId =
      currentPipelineId === pipelineId ? next[0].id : currentPipelineId;
    syncState(next, nextActiveId);

    try {
      const client = getSupabaseClient();
      const {
        data: { session },
      } = await client.auth.getSession();
      if (!session?.user) return;

      const activeCrmId = getActiveCrmId();
      const dbPipeId = await getDbPipelineId(
        client,
        pipelineId,
        targetPipe?.nome,
      );
      if (dbPipeId) {
        await client
          .from("pipelines")
          .delete()
          .eq("id", dbPipeId)
          .eq("crm_id", activeCrmId);
      }
    } catch (err) {
      console.error("Erro ao excluir funil no Supabase:", err);
    }
  };

  const addStage = async (pipelineId: string, nomeEtapa: string) => {
    const cleanName = nomeEtapa.trim();
    if (!cleanName) return;

    const targetPipe =
      pipelines.find((p) => p.id === pipelineId) || currentPipeline;
    const slug = generateStageSlug(cleanName, targetPipe.etapas);

    const newStage: DealStage = { value: slug, label: cleanName };
    const next = pipelines.map((pipe) => {
      if (pipe.id !== targetPipe.id) return pipe;
      return { ...pipe, etapas: [...pipe.etapas, newStage] };
    });

    syncState(next, currentPipelineId);

    try {
      const client = getSupabaseClient();
      const {
        data: { session },
      } = await client.auth.getSession();
      if (!session?.user) return;

      const dbPipeId = await getDbPipelineId(
        client,
        targetPipe.id,
        targetPipe.nome,
      );
      if (dbPipeId) {
        await client.from("pipeline_stages").insert({
          pipeline_id: dbPipeId,
          name: cleanName,
          slug: slug,
          order_nr: targetPipe.etapas.length + 1,
        });
      }
    } catch (err) {
      console.error("Erro ao adicionar etapa no Supabase:", err);
    }
  };

  const deleteStage = async (pipelineId: string, stageValue: string) => {
    const targetPipe =
      pipelines.find((p) => p.id === pipelineId) || currentPipeline;
    if (targetPipe.etapas.length <= 1) return;

    const next = pipelines.map((pipe) => {
      if (pipe.id !== targetPipe.id) return pipe;
      return {
        ...pipe,
        etapas: pipe.etapas.filter((s) => s.value !== stageValue),
      };
    });

    syncState(next, currentPipelineId);

    try {
      const client = getSupabaseClient();
      const {
        data: { session },
      } = await client.auth.getSession();
      if (!session?.user) return;

      const dbPipeId = await getDbPipelineId(
        client,
        targetPipe.id,
        targetPipe.nome,
      );
      if (dbPipeId) {
        await client
          .from("pipeline_stages")
          .delete()
          .eq("pipeline_id", dbPipeId)
          .eq("slug", stageValue);
      }
    } catch (err) {
      console.error("Erro ao excluir etapa no Supabase:", err);
    }
  };

  const updateStage = async (
    pipelineId: string,
    stageValue: string,
    newLabel: string,
  ) => {
    const targetPipe =
      pipelines.find((p) => p.id === pipelineId) || currentPipeline;

    const next = pipelines.map((pipe) => {
      if (pipe.id !== targetPipe.id) return pipe;
      return {
        ...pipe,
        etapas: pipe.etapas.map((s) =>
          s.value === stageValue ? { ...s, label: newLabel } : s,
        ),
      };
    });

    syncState(next, currentPipelineId);

    try {
      const client = getSupabaseClient();
      const {
        data: { session },
      } = await client.auth.getSession();
      if (!session?.user) return;

      const dbPipeId = await getDbPipelineId(
        client,
        targetPipe.id,
        targetPipe.nome,
      );
      if (dbPipeId) {
        await client
          .from("pipeline_stages")
          .update({ name: newLabel })
          .eq("pipeline_id", dbPipeId)
          .eq("slug", stageValue);
      }
    } catch (err) {
      console.error("Erro ao atualizar etapa no Supabase:", err);
    }
  };

  const updatePipelineStages = async (
    pipelineId: string,
    newStages: DealStage[],
    newPipelineName?: string,
  ) => {
    const targetPipe =
      pipelines.find((p) => p.id === pipelineId) || currentPipeline;
    const finalName = newPipelineName?.trim() || targetPipe.nome;

    const next = pipelines.map((pipe) => {
      if (pipe.id !== targetPipe.id) return pipe;
      return {
        ...pipe,
        nome: finalName,
        etapas: newStages,
      };
    });

    syncState(next, currentPipelineId);

    try {
      const client = getSupabaseClient();
      const {
        data: { session },
      } = await client.auth.getSession();
      if (!session?.user) return;

      const dbPipeId = await getDbPipelineId(
        client,
        targetPipe.id,
        targetPipe.nome,
      );
      if (dbPipeId) {
        // Atualiza nome do funil se alterado
        if (
          newPipelineName?.trim() &&
          newPipelineName.trim() !== targetPipe.nome
        ) {
          await client
            .from("pipelines")
            .update({ name: newPipelineName.trim() })
            .eq("id", dbPipeId);
        }

        // Exclui etapas que foram deletadas na UI
        const activeSlugs = newStages.map((s) => s.value);
        const { data: currentRemoteStages } = await client
          .from("pipeline_stages")
          .select("slug")
          .eq("pipeline_id", dbPipeId);

        if (currentRemoteStages && currentRemoteStages.length > 0) {
          const toDelete = currentRemoteStages
            .map((s: any) => s.slug)
            .filter((sl: string) => !activeSlugs.includes(sl));

          if (toDelete.length > 0) {
            await client
              .from("pipeline_stages")
              .delete()
              .eq("pipeline_id", dbPipeId)
              .in("slug", toDelete);
          }
        }

        // Atualiza/Upsert das etapas com os nomes e ordenações atualizados
        for (let i = 0; i < newStages.length; i++) {
          const st = newStages[i];
          await client.from("pipeline_stages").upsert(
            {
              pipeline_id: dbPipeId,
              name: st.label,
              slug: st.value,
              order_nr: i + 1,
            },
            { onConflict: "pipeline_id,slug" },
          );
        }
      }
    } catch (err) {
      console.error("Erro ao atualizar etapas no Supabase:", err);
    }
  };

  return {
    pipelines,
    currentPipeline,
    currentPipelineId,
    isLoadingPipelines,
    setCurrentPipelineId: handleSelectPipeline,
    addPipeline,
    deletePipeline,
    addStage,
    deleteStage,
    updateStage,
    updatePipelineStages,
  };
};
